/**
 * Auth Service
 * Swagger: /auth/*, /user/change-password, /terms/list
 * apiClient를 직접 import하지 않고 fetch 사용 (순환 의존성 방지)
 */

import type {
	SendVerificationEmailRequestDto,
	SendVerificationEmailResponseDto,
	VerifyVerificationCodeRequestDto,
	VerifyVerificationCodeResponseDto,
	PasswordResetRequestDto,
	PasswordResetResponseDto,
	SendDeletionCodeResponseDto,
	VerifyDeletionCodeRequestDto,
	VerifyDeletionCodeResponseDto,
	ChangePasswordResponseDto,
} from '../types/api/auth';
import type { GetTermsListResponseDto } from '../types/terms';

import { API_BASE_URL, getAuthRefreshUrl } from '../config/apiBaseUrl';
import { normalizeBearerTokenForAuthorization } from '../utils/bearerTokenNormalize';

function getAccessToken(): string | null {
  const raw = localStorage.getItem('medicrew_access_token');
  if (raw == null || raw === '') return null;
  return normalizeBearerTokenForAuthorization(raw);
}

function authHeaders(requireAuth = false): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (requireAuth) {
    const token = getAccessToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let msg = `요청 실패: ${res.status}`;
    try {
      const body = await res.json();
      if (body?.message) msg = body.message;
    } catch {
      // ignore
    }
    throw new Error(msg);
  }
  return res.json() as Promise<T>;
}

/** POST /auth/access-token */
export const refreshAccessToken = async (): Promise<{
  accessToken: string;
  refreshToken?: string;
} | null> => {
  const refreshToken = localStorage.getItem('medicrew_refresh_token');
  if (!refreshToken) return null;
  const res = await fetch(getAuthRefreshUrl(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });
  return handleResponse(res);
};

/** POST /auth/send-verification-email */
export const sendVerificationEmail = async (
  data: SendVerificationEmailRequestDto
): Promise<SendVerificationEmailResponseDto> => {
  const res = await fetch(`${API_BASE_URL}/auth/send-verification-email`, {
    method: 'POST',
    headers: authHeaders(true),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
};

/** POST /auth/verify-verification-code */
export const verifyVerificationCode = async (
  data: VerifyVerificationCodeRequestDto
): Promise<VerifyVerificationCodeResponseDto> => {
  const res = await fetch(`${API_BASE_URL}/auth/verify-verification-code`, {
    method: 'POST',
    headers: authHeaders(true),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
};

/** POST /auth/password-reset */
export const requestPasswordReset = async (
  data: PasswordResetRequestDto
): Promise<PasswordResetResponseDto> => {
  const res = await fetch(`${API_BASE_URL}/auth/password-reset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return handleResponse(res);
};

/** POST /auth/send-deletion-code */
export const sendDeletionCode = async (): Promise<SendDeletionCodeResponseDto> => {
  const res = await fetch(`${API_BASE_URL}/auth/send-deletion-code`, {
    method: 'POST',
    headers: authHeaders(true),
  });
  return handleResponse(res);
};

/** POST /auth/verify-deletion-code */
export const verifyDeletionCode = async (
  data: VerifyDeletionCodeRequestDto
): Promise<VerifyDeletionCodeResponseDto> => {
  const res = await fetch(`${API_BASE_URL}/auth/verify-deletion-code`, {
    method: 'POST',
    headers: authHeaders(true),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
};

/** GET /terms/list */
export const getTermsList = async (): Promise<GetTermsListResponseDto> => {
  const res = await fetch(`${API_BASE_URL}/terms/list`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  return handleResponse(res);
};
